import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export const Dropdown = DropdownMenu.Root
export const DropdownTrigger = DropdownMenu.Trigger
export const DropdownGroup = DropdownMenu.Group
export const DropdownSub = DropdownMenu.Sub
export const DropdownRadioGroup = DropdownMenu.RadioGroup

export const DropdownContent = forwardRef<
  ElementRef<typeof DropdownMenu.Content>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenu.Portal>
    <DropdownMenu.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-popover min-w-[10rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-popover',
        'animate-zoom-in-95',
        className,
      )}
      {...props}
    />
  </DropdownMenu.Portal>
))
DropdownContent.displayName = 'DropdownContent'

const itemBase =
  'relative flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-body text-gray-700 outline-none focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50'

export const DropdownItem = forwardRef<
  ElementRef<typeof DropdownMenu.Item>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Item> & { tone?: 'default' | 'danger' }
>(({ className, tone = 'default', ...props }, ref) => (
  <DropdownMenu.Item
    ref={ref}
    className={cn(
      itemBase,
      tone === 'danger' && 'text-red-600 focus:bg-red-50 focus:text-red-700',
      className,
    )}
    {...props}
  />
))
DropdownItem.displayName = 'DropdownItem'

export const DropdownCheckboxItem = forwardRef<
  ElementRef<typeof DropdownMenu.CheckboxItem>,
  ComponentPropsWithoutRef<typeof DropdownMenu.CheckboxItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenu.CheckboxItem
    ref={ref}
    className={cn(itemBase, 'pl-7', className)}
    {...props}
  >
    <span className="absolute left-1.5 flex h-4 w-4 items-center justify-center">
      <DropdownMenu.ItemIndicator>
        <Check className="h-3.5 w-3.5" />
      </DropdownMenu.ItemIndicator>
    </span>
    {children}
  </DropdownMenu.CheckboxItem>
))
DropdownCheckboxItem.displayName = 'DropdownCheckboxItem'

export const DropdownLabel = forwardRef<
  ElementRef<typeof DropdownMenu.Label>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenu.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-caption font-semibold uppercase tracking-wide text-gray-500', className)}
    {...props}
  />
))
DropdownLabel.displayName = 'DropdownLabel'

export const DropdownSeparator = forwardRef<
  ElementRef<typeof DropdownMenu.Separator>,
  ComponentPropsWithoutRef<typeof DropdownMenu.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenu.Separator ref={ref} className={cn('my-1 h-px bg-gray-200', className)} {...props} />
))
DropdownSeparator.displayName = 'DropdownSeparator'

export const DropdownSubTrigger = forwardRef<
  ElementRef<typeof DropdownMenu.SubTrigger>,
  ComponentPropsWithoutRef<typeof DropdownMenu.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenu.SubTrigger ref={ref} className={cn(itemBase, className)} {...props}>
    {children}
    <ChevronRight className="ml-auto h-4 w-4" />
  </DropdownMenu.SubTrigger>
))
DropdownSubTrigger.displayName = 'DropdownSubTrigger'

export const DropdownSubContent = forwardRef<
  ElementRef<typeof DropdownMenu.SubContent>,
  ComponentPropsWithoutRef<typeof DropdownMenu.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenu.SubContent
    ref={ref}
    className={cn(
      'z-popover min-w-[10rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 shadow-popover animate-zoom-in-95',
      className,
    )}
    {...props}
  />
))
DropdownSubContent.displayName = 'DropdownSubContent'
